import React, { useState } from 'react';
import axios from 'axios';
import './TechnicalReportDetail.css';

const TechnicalReportDetail = ({ report, onUpdate, onClose, onOpenChat }) => {
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState(report.status);

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/technical-reports/${report._id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStatus(newStatus);
      onUpdate();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      alert('Error al actualizar el estado: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return '#ffc107';
      case 'en_proceso': return '#17a2b8';
      case 'completado': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'instalacion': return '🔧';
      case 'averia': return '⚠️';
      case 'mantenimiento': return '🔨';
      default: return '📋';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgente': return '#dc3545';
      case 'alta': return '#fd7e14';
      case 'media': return '#ffc107';
      case 'baja': return '#28a745';
      default: return '#6c757d';
    }
  };

  return (
    <div className="technical-report-detail">
      <div className="detail-header">
        <div>
          <h2>{report.ticketNumber}</h2>
          <div className="detail-meta">
            <span className="detail-type">
              {getTypeIcon(report.type)} {report.type === 'instalacion' ? 'Instalación' :
                                            report.type === 'averia' ? 'Avería' :
                                            report.type === 'mantenimiento' ? 'Mantenimiento' :
                                            'Otro'}
            </span>
            <span className="detail-priority" style={{ color: getPriorityColor(report.priority) }}>
              {report.priority === 'urgente' ? '🔴 Urgente' :
               report.priority === 'alta' ? '🟠 Alta' :
               report.priority === 'media' ? '🟡 Media' :
               '🟢 Baja'}
            </span>
          </div>
        </div>
        {onOpenChat && report.phoneNumber && (
          <button
            className="btn-chat-detail"
            onClick={() => { onOpenChat(report.phoneNumber); }}
            title="Abrir chat del cliente"
            style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: "6px", padding: "6px 14px", cursor: "pointer", fontWeight: "bold", marginRight: "8px" }}
          >
            💬 Chat
          </button>
        )}
        <button className="btn-close-detail" onClick={onClose}>✕</button>
      </div>

      <div className="detail-content">
        <div className="detail-section">
          <h3>Estado del Reporte</h3>
          <div className="status-selector">
            <button
              className={`status-btn ${status === 'pendiente' ? 'active' : ''}`}
              onClick={() => handleStatusChange('pendiente')}
              disabled={updating}
              style={{ backgroundColor: status === 'pendiente' ? '#ffc107' : '#f0f0f0' }}
            >
              Pendiente
            </button>
            <button
              className={`status-btn ${status === 'en_proceso' ? 'active' : ''}`}
              onClick={() => handleStatusChange('en_proceso')}
              disabled={updating}
              style={{ backgroundColor: status === 'en_proceso' ? '#17a2b8' : '#f0f0f0' }}
            >
              En Proceso
            </button>
            <button
              className={`status-btn ${status === 'completado' ? 'active' : ''}`}
              onClick={() => handleStatusChange('completado')}
              disabled={updating}
              style={{ backgroundColor: status === 'completado' ? '#28a745' : '#f0f0f0' }}
            >
              Completado
            </button>
            <button
              className={`status-btn ${status === 'cancelado' ? 'active' : ''}`}
              onClick={() => handleStatusChange('cancelado')}
              disabled={updating}
              style={{ backgroundColor: status === 'cancelado' ? '#dc3545' : '#f0f0f0' }}
            >
              Cancelado
            </button>
          </div>
        </div>

        <div className="detail-section">
          <h3>Información del Cliente</h3>
          <div className="detail-info-grid">
            <div className="info-item">
              <label>Nombre:</label>
              <span>{report.clientName}</span>
            </div>
            <div className="info-item">
              <label>Teléfono:</label>
              <span>{report.phoneNumber}</span>
            </div>
            <div className="info-item">
              <label>ID Mikrowisp:</label>
              <span>{report.mikrowispId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Dirección:</label>
              <span>{report.clientAddress || 'N/A'}</span>
            </div>
            {report.servicePlan && (
              <div className="info-item">
                <label>Plan:</label>
                <span>{report.servicePlan}</span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h3>Detalles del Reporte</h3>
          <div className="detail-info-grid">
            <div className="info-item full-width">
              <label>Título:</label>
              <span>{report.title}</span>
            </div>
            <div className="info-item full-width">
              <label>Descripción:</label>
              <div className="description-text">{report.description}</div>
            </div>
            {report.technician && (
              <div className="info-item">
                <label>Técnico:</label>
                <span>{report.technician}</span>
              </div>
            )}
            {report.technicianPhone && (
              <div className="info-item">
                <label>Teléfono Técnico:</label>
                <span>{report.technicianPhone}</span>
              </div>
            )}
            {report.scheduledDate && (
              <div className="info-item">
                <label>Fecha Programada:</label>
                <span>{formatDate(report.scheduledDate)}</span>
              </div>
            )}
            {report.completedDate && (
              <div className="info-item">
                <label>Fecha Completado:</label>
                <span>{formatDate(report.completedDate)}</span>
              </div>
            )}
          </div>
        </div>

        {report.notes && (
          <div className="detail-section">
            <h3>Notas</h3>
            <div className="notes-text">{report.notes}</div>
          </div>
        )}

        {report.internalNotes && (
          <div className="detail-section">
            <h3>Notas Internas</h3>
            <div className="notes-text internal">{report.internalNotes}</div>
          </div>
        )}

        {report.tags && report.tags.length > 0 && (
          <div className="detail-section">
            <h3>Etiquetas</h3>
            <div className="tags-list">
              {report.tags.map((tag, index) => (
                <span key={index} className="tag-badge">{tag}</span>
              ))}
            </div>
          </div>
        )}

        <div className="detail-section">
          <h3>Información del Sistema</h3>
          <div className="detail-info-grid">
            <div className="info-item">
              <label>Creado:</label>
              <span>{formatDate(report.createdAt)}</span>
            </div>
            <div className="info-item">
              <label>Última Actualización:</label>
              <span>{formatDate(report.updatedAt)}</span>
            </div>
            {report.createdBy && (
              <div className="info-item">
                <label>Creado por:</label>
                <span>{report.createdBy}</span>
              </div>
            )}
            {report.assignedTo && (
              <div className="info-item">
                <label>Asignado a:</label>
                <span>{report.assignedTo}</span>
              </div>
            )}
          </div>
        </div>

        {report.whatsappMessages && report.whatsappMessages.length > 0 && (
          <div className="detail-section">
            <h3>Mensajes de WhatsApp ({report.whatsappMessages.length})</h3>
            <div className="whatsapp-messages-list">
              {report.whatsappMessages.map((msg, index) => (
                <div key={index} className={`whatsapp-message ${msg.direction}`}>
                  <div className="message-direction">
                    {msg.direction === 'outbound' ? '📤 Enviado' : '📥 Recibido'}
                  </div>
                  <div className="message-content">{msg.content}</div>
                  <div className="message-time">{formatDate(msg.timestamp)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalReportDetail;




